import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useDocuments } from "pages/DevelopWorkspace/contexts/DocumentsContext/hooks/useDocuments";
import { useScripts } from "pages/DevelopWorkspace/contexts/ScriptsContext/ScriptsContext";
import { getDocumentScript } from "pages/DevelopWorkspace/helpers/getDocumentScript";
import {
  TabGroup,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import { ConfirmationModal } from "components/ConfirmationModal/ConfirmationModal";

interface Modal {
  title: string;
  message: string;
  confirmButtonText: string;
  onConfirm: () => void;
}

const useDocumentsActionConfirmation = () => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);
  const {
    state: { documents },
    actions: {
      removeDocument,
      removeAllDocuments,
      removeTabGroup,
      createTabGroup,
      setDocumentTabGroup,
    },
  } = useDocuments();
  const { state: scriptsState } = useScripts();

  const documentHasContent = (document: WorkspaceDocument) => {
    const script = getDocumentScript(document, scriptsState);

    // We check editorState.doc to determine if a document has content.
    return !!script?.editorState && script.editorState.doc !== "";
  };

  const handleDeleteAllDocuments = (exceptDocumentIds: string[]) => {
    const someDocumentHasContent = documents.some(document => {
      if (exceptDocumentIds.includes(document.id)) {
        return false;
      }

      return documentHasContent(document);
    });

    if (!someDocumentHasContent) {
      removeAllDocuments(exceptDocumentIds);
      return;
    }

    if (exceptDocumentIds.length === 0) {
      setModal({
        title: t("delete_document_confirmation.all.title"),
        message: t("delete_document_confirmation.all.message"),
        confirmButtonText: t("delete_document_confirmation.all.confirm"),
        onConfirm: () => {
          setModal(null);
          removeAllDocuments([]);
        },
      });
      return;
    }

    // all_other
    setModal({
      title: t("delete_document_confirmation.all_other.title"),
      message: t("delete_document_confirmation.all_other.message"),
      confirmButtonText: t("delete_document_confirmation.all_other.confirm"),
      onConfirm: () => {
        setModal(null);
        removeAllDocuments(exceptDocumentIds);
      },
    });
  };

  const handleDeleteDocument = (documentId: string) => {
    const document = documents.find(doc => doc.id === documentId);

    if (!document) {
      return;
    }

    if (!documentHasContent(document)) {
      removeDocument(document.id);
      return;
    }

    setModal({
      title: t("delete_document_confirmation.single.title"),
      message: t("delete_document_confirmation.single.message"),
      confirmButtonText: t("delete_document_confirmation.single.confirm"),
      onConfirm: () => {
        setModal(null);
        removeDocument(document.id);
      },
    });
  };

  const handleUngroupTabGroup = (tabGroupId: string) => {
    setModal({
      title: t("delete_tab_group_confirmation.group.title"),
      message: t("delete_tab_group_confirmation.group.message"),
      confirmButtonText: t("delete_tab_group_confirmation.group.confirm"),
      onConfirm: () => {
        setModal(null);
        removeTabGroup(tabGroupId);
      },
    });
  };

  const handleDeleteTabGroupDocuments = (tabGroupId: string) => {
    const exceptDocumentIds = documents
      .filter(document => document.groupId !== tabGroupId)
      .map(document => document.id);

    setModal({
      title: t("delete_tab_group_confirmation.documents.title"),
      message: t("delete_tab_group_confirmation.documents.message"),
      confirmButtonText: t("delete_tab_group_confirmation.documents.confirm"),
      onConfirm: () => {
        setModal(null);
        removeAllDocuments(exceptDocumentIds);
      },
    });
  };

  const confirmEmptyingAGroup = (documentIds: string[], move: () => void) => {
    const emptiesAGroup = documentIds.some(documentId => {
      const groupId = documents.find(doc => doc.id === documentId)?.groupId;

      return (
        !!groupId &&
        !documents.some(doc => doc.groupId === groupId && doc.id !== documentId)
      );
    });

    if (!emptiesAGroup) {
      move();
      return;
    }

    setModal({
      title: t("delete_tab_group_confirmation.last_document.title"),
      message: t("delete_tab_group_confirmation.last_document.message"),
      confirmButtonText: t(
        "delete_tab_group_confirmation.last_document.confirm"
      ),
      onConfirm: () => {
        setModal(null);
        move();
      },
    });
  };

  const handleSetDocumentTabGroup = (
    documentId: string,
    groupId: string | null
  ) => {
    const document = documents.find(doc => doc.id === documentId);

    if (!document || document.groupId === groupId) {
      return;
    }

    confirmEmptyingAGroup([documentId], () => {
      setDocumentTabGroup(documentId, groupId);
    });
  };

  const handleCreateTabGroup = (
    tabGroup: Omit<TabGroup, "id" | "collapsed">,
    documentIds: string[]
  ) => {
    confirmEmptyingAGroup(documentIds, () => {
      createTabGroup(tabGroup, documentIds);
    });
  };

  const renderModal = () => {
    if (!modal) {
      return null;
    }

    return (
      <ConfirmationModal
        onClose={() => {
          setModal(null);
        }}
        onConfirm={modal.onConfirm}
        title={modal.title}
        message={modal.message}
        confirmButtonOptions={{
          text: modal.confirmButtonText,
        }}
      />
    );
  };

  const modalMarkup = renderModal();

  return {
    handleDeleteDocument,
    handleDeleteAllDocuments,
    handleUngroupTabGroup,
    handleDeleteTabGroupDocuments,
    handleSetDocumentTabGroup,
    handleCreateTabGroup,
    modalMarkup,
  };
};

export { useDocumentsActionConfirmation };
